import { useInfiniteQuery, useQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import {
  listConversationMembers,
  loadMessagesPage,
} from '@/lib/messaging/messaging-client';
import { useAuth } from '@/contexts/AuthContext';
import { useRealtimeHealth } from '@/hooks/messaging/useRealtimeHealth';
import { carryLocalMessages } from '@/lib/messaging/optimistic-messages';
import type { MessageWithAttachments } from '@/types/messaging';

const PAGE_SIZE = 50;

export const conversationMessagesQueryKey = (id: string) =>
  ['messaging', 'messages', id] as const;
export const conversationMembersQueryKey = (id: string) =>
  ['messaging', 'members', id] as const;

export const useConversationMembers = (conversationId: string | null) => {
  const { profile } = useAuth();
  return useQuery({
    queryKey: conversationId ? conversationMembersQueryKey(conversationId) : ['messaging', 'members', 'none'],
    enabled: !!conversationId && !!profile?.id,
    staleTime: 60_000,
    queryFn: async () => {
      if (!conversationId) return [];
      const { data, error } = await listConversationMembers(conversationId);
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });
};

export const useConversationMessages = (conversationId: string | null) => {
  const { profile } = useAuth();
  const qc = useQueryClient();
  const realtimeStatus = useRealtimeHealth(conversationId);

  // When the focused-conversation realtime channel is subscribed and
  // patching the cache directly, we can throttle the polling fallback
  // way down. When it's closed or unknown, keep the 8s safety net.
  const refetchInterval =
    realtimeStatus === 'subscribed' ? 60_000 : 8_000;

  return useInfiniteQuery({
    queryKey: conversationId ? conversationMessagesQueryKey(conversationId) : ['messaging', 'messages', 'none'],
    enabled: !!conversationId && !!profile?.id,
    initialPageParam: null as string | null,
    refetchInterval,
    refetchOnWindowFocus: true,
    getNextPageParam: (lastPage) => {
      if (!lastPage || lastPage.length < PAGE_SIZE) return undefined;
      return lastPage[lastPage.length - 1]?.created_at ?? undefined;
    },
    queryFn: async ({ pageParam }): Promise<MessageWithAttachments[]> => {
      if (!conversationId) return [];
      const { data, error } = await loadMessagesPage(conversationId, {
        before: pageParam ?? undefined,
        limit: PAGE_SIZE,
      });
      if (error) throw new Error(error.message);
      const rows = data ?? [];
      if (pageParam !== null) return rows;
      // Page 0 is replaced wholesale by every poll / focus refetch. Keep any
      // bubble this device painted but the server has not confirmed yet,
      // or a send would blink out and back in when a poll lands mid-flight.
      const previous = qc.getQueryData<InfiniteData<MessageWithAttachments[], string | null>>(
        conversationMessagesQueryKey(conversationId),
      )?.pages[0];
      return carryLocalMessages(rows, previous, 'newest-first');
    },
  });
};
